import React from "react";
import classNames from "classnames";
import dynamic from "next/dynamic";

import SidePanel from "@/shared/components/SidePanel/SidePanel";
import CloseButton from "@/shared/components/Button/CloseButton/CloseButton";
import { Button, ButtonQRCode } from "@/components/index";
import ButtonInstallPWA from "@/components/Button/ButtonInstallPWA";
import LinkButton from "@/components/Button/LinkButton";
import { usePagePropsContext } from "@/contexts/pageProps";
import useMediaQuery, { MEDIA_QUERIES } from "@/shared/hooks/useMediaQuery";
import IconView from "@/shared/components/IconView/IconView";
import ButtonShare from "../SharePopup/ButtonShare";
import LanguageUISelector from "../settingsInterface/LanguageUISelector";

import styles from "./PanelHeaderMenu.module.scss";

const ButtonTheme = dynamic(() => import("@/components/DropDown/ButtonTheme"), {
  ssr: false,
});

const PanelHeaderMenu = ({
  isOpen,
  onClose,
  onClickLink,
  links,
  isShowQR = true,
  isShowPWA = true,
  isHiddenByCSS = false,
}) => {
  const { urlOrigin, isLanding, isBigLanding, landingBookViewConfig } = usePagePropsContext();
  const mediaMobile = useMediaQuery(MEDIA_QUERIES.untilTablet);
  const isMobile = isLanding ? true : mediaMobile;

  const jsxBtnQR = (
    <ButtonQRCode isHeaderIcon={isMobile} withLabel={!isMobile} className={styles["btn-qr"]} />
  );

  const jsxLinks = links.map((item) => {
    const { id, icon, href, isOnLineOnly, label, title, onClick } = item;

    const isExternal = href && href.startsWith("http") && !href.includes(urlOrigin);

    const Wrapper = href ? LinkButton : Button;

    return (
      <Wrapper
        key={id ?? href}
        id={id ?? ""}
        paddingLess
        className={classNames(styles["link"], "menu-mobile-links")}
        isOnLineOnly={isOnLineOnly}
        variant={LinkButton.variant.clear}
        color={LinkButton.color.icon}
        title={title ?? label}
        target={isExternal ? "_blank" : undefined}
        // href to prevent error "no href value"
        href={href}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
          }
          if (onClickLink) {
            onClickLink(e, item);
          }
        }}>
        {icon && <IconView className={styles["item-icon"]} icon={icon} />}
        <span dangerouslySetInnerHTML={{ __html: label }} />
      </Wrapper>
    );
  });

  const jsxBtnTheme = (!isLanding || landingBookViewConfig?.hasDarkMode) && (
    <ButtonTheme className={styles["dd-theme"]} />
  );

  const shareData = "window" in global && window.history.state?.shareData;
  const rightSideBtn1 =
    shareData && isLanding ? (
      <ButtonShare
        text={shareData.text}
        refCode={shareData.refCode}
        title={shareData.title}
        url={shareData.url}
        className={styles["dd-theme"]}
      />
    ) : (
      jsxBtnTheme
    );

  return (
    <SidePanel
      isUseLockBodyScroll
      isHiddenByCSS={isHiddenByCSS}
      preventCoverHeader={!isMobile}
      isOpen={isOpen}
      onClose={onClose}
      className={styles["root"]}
      contentProps={{
        className: styles["content"],
        tag: "nav",
      }}>
      <div className={styles["inner"]}>
        {isMobile && isShowQR && !isBigLanding && jsxBtnQR}

        {jsxLinks}

        <LanguageUISelector
          onClick={onClickLink}
          render="small"
          className={classNames(styles["link"] )}
        />

        {isMobile && rightSideBtn1}

        {isLanding && isShowPWA && !isBigLanding && <ButtonInstallPWA />}

        <div className={styles["wrap-actions"]}>
          {!isMobile && jsxBtnQR}
          <CloseButton
            className={classNames(styles["btn-close"], "menu-mobile-links")}
            onClick={onClose}
          />
        </div>
      </div>
    </SidePanel>
  );
};

export default PanelHeaderMenu;
